import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet.markercluster"
import { get } from "@rails/request.js"

export default class extends Controller {
    static targets = ["container", "loading", "list"]
    static values = {
        url: String,
        center: Array, // [lat, lng]
        zoom: Number,
        labels: Object
    }

    connect() {
        console.log("Map controller connected")
        this.layers = {}

        // Default center (Europe/Rome roughly, or world view)
        const defaultCenter = this.hasCenterValue ? this.centerValue : [20, 0]
        const defaultZoom = this.hasZoomValue ? this.zoomValue : 2

        // Initialize map
        this.map = L.map(this.containerTarget, {
            center: defaultCenter,
            zoom: defaultZoom,
            scrollWheelZoom: false,
            zoomControl: false // We will add it later in a better position
        })

        L.control.zoom({ position: 'bottomright' }).addTo(this.map)

        // Add Legend
        this.addLegend()

        // Add CartoDB Positron tiles
        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
            subdomains: 'abcd',
            maxZoom: 20
        }).addTo(this.map)

        // Fix for tiles not loading correctly in some containers
        setTimeout(() => {
            this.map.invalidateSize()
        }, 200)

        // Load data
        this.loadLocations()
    }

    addLegend() {
        const legend = L.control({ position: 'bottomleft' })
        const labels = this.labelsValue || {
            critical: 'Critical',
            moderate: 'Moderate',
            low: 'Low',
            plant: 'Plant',
            vendor: 'Vendor'
        }

        legend.onAdd = () => {
            const div = L.DomUtil.create('div', 'map-legend shadow-lg border border-base-200 p-3 rounded-box bg-base-100/90 backdrop-blur-md')
            div.innerHTML = `
                <div class="flex flex-col gap-2">
                    <div class="flex items-center gap-2">
                        <span class="w-3 h-3 rounded-full bg-error animate-pulse"></span>
                        <span class="text-[10px] uppercase font-bold text-base-content/70">${labels.critical}</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <span class="w-3 h-3 rounded-full bg-warning"></span>
                        <span class="text-[10px] uppercase font-bold text-base-content/70">${labels.moderate}</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <span class="w-3 h-3 rounded-full bg-success"></span>
                        <span class="text-[10px] uppercase font-bold text-base-content/70">${labels.low}</span>
                    </div>
                    <div class="mt-1 pt-1 border-t border-base-200">
                        <div class="flex items-center gap-2">
                            <span class="w-3 h-3 rounded-full bg-primary"></span>
                            <span class="text-[10px] uppercase font-bold text-base-content/70">${labels.plant}</span>
                        </div>
                    </div>
                </div>
            `
            return div
        }
        legend.addTo(this.map)
    }

    disconnect() {
        if (this.map) {
            this.map.remove()
        }
    }

    async loadLocations() {
        try {
            const response = await get(this.urlValue, {
                responseKind: "json",
                headers: { "Accept": "application/json" }
            })

            if (!response.ok) throw new Error("Network response was not ok")

            const data = await response.json
            this.geoJsonData = data
            this.renderGeoJson(data)
            this.populateSidebar(data)

        } catch (error) {
            console.error("Error loading map locations:", error)
        } finally {
            if (this.hasLoadingTarget) {
                this.loadingTarget.classList.add('hidden')
            }
        }
    }

    renderGeoJson(data) {
        if (!data.features || data.features.length === 0) return

        this.markersCluster = L.markerClusterGroup({
            zoomToBoundsOnClick: true,
            maxClusterRadius: 40,
            spiderfyOnMaxZoom: true,
            disableClusteringAtZoom: 16
        })

        const geoJsonLayer = L.geoJSON(data, {
            style: (feature) => {
                if (feature.geometry.type === 'LineString') {
                    const risk = feature.properties.risk_score || 0
                    let color = "#cbd5e1"
                    if (risk >= 75) color = "#ef4444"
                    else if (risk >= 50) color = "#f59e0b"
                    else color = "#3b82f6"

                    return {
                        color: color,
                        weight: 2,
                        opacity: 0.6,
                        dashArray: '5, 10',
                        lineCap: 'round'
                    }
                }
            },
            pointToLayer: (feature, latlng) => {
                if (feature.geometry.type === 'Point') {
                    const marker = L.marker(latlng, {
                        icon: this.createCustomIcon(feature),
                        zIndexOffset: feature.properties.type === 'user' ? 1000 : 0
                    })
                    return marker
                }
            },
            onEachFeature: (feature, layer) => {
                const id = feature.properties.id || `route_${feature.properties.vendor_name}_${feature.properties.plant_name}`
                this.layers[id] = layer

                if (feature.properties.type === 'route') {
                    this.bindRoutePopup(feature, layer)
                    layer.addTo(this.map)
                } else {
                    this.bindTooltipAndClick(feature, layer)
                    this.markersCluster.addLayer(layer)
                }
            }
        })

        this.map.addLayer(this.markersCluster)

        if (geoJsonLayer.getLayers().length > 0) {
            this.map.fitBounds(geoJsonLayer.getBounds(), {
                padding: [50, 50],
                maxZoom: 10,
                animate: true
            })
        }
    }

    populateSidebar(data, filter = "") {
        if (!this.hasListTarget) return

        const plants = data.features.filter(f => f.properties.type === 'user')
        const vendors = data.features.filter(f => f.properties.type === 'vendor')
        const routes = data.features.filter(f => f.properties.type === 'route')

        const labels = this.labelsValue
        let html = ""

        const filterLower = filter.toLowerCase()

        // Plants Section
        const filteredPlants = plants.filter(f => f.properties.display_name.toLowerCase().includes(filterLower))
        if (filteredPlants.length > 0) {
            html += `<div class="section"><h4 class="text-[11px] font-black uppercase text-base-content/40 mb-2 px-2 tracking-widest">${labels.sidebar_plants}</h4><div class="space-y-1">`
            filteredPlants.forEach(f => html += this.renderSidebarItem(f))
            html += `</div></div>`
        }

        // Vendors Section
        const filteredVendors = vendors.filter(f => f.properties.display_name.toLowerCase().includes(filterLower))
        if (filteredVendors.length > 0) {
            html += `<div class="section"><h4 class="text-[11px] font-black uppercase text-base-content/40 mb-2 px-2 tracking-widest">${labels.sidebar_vendors}</h4><div class="space-y-1">`
            filteredVendors.forEach(f => html += this.renderSidebarItem(f))
            html += `</div></div>`
        }

        // Routes Section
        const filteredRoutes = routes.filter(f =>
            f.properties.vendor_name.toLowerCase().includes(filterLower) ||
            f.properties.plant_name.toLowerCase().includes(filterLower)
        )
        if (filteredRoutes.length > 0) {
            html += `<div class="section"><h4 class="text-[11px] font-black uppercase text-base-content/40 mb-2 px-2 tracking-widest">${labels.sidebar_routes}</h4><div class="space-y-1">`
            filteredRoutes.forEach(f => {
                const id = `route_${f.properties.vendor_name}_${f.properties.plant_name}`
                html += `
                    <button class="flex flex-col w-full text-left p-2 rounded-lg hover:bg-base-200 transition-colors group border border-transparent hover:border-base-300"
                            data-action="click->map#focusElement"
                            data-id="${id}">
                        <div class="flex items-center justify-between w-full mb-1">
                            <span class="text-xs font-bold leading-none truncate pr-2">${f.properties.vendor_name} → ${f.properties.plant_name}</span>
                            <span class="badge badge-ghost badge-xs font-mono opacity-50 text-[9px]">${f.properties.quota}%</span>
                        </div>
                        <div class="h-1 w-full bg-base-200 rounded-full overflow-hidden">
                             <div class="h-full ${f.properties.risk_score >= 75 ? 'bg-error' : (f.properties.risk_score >= 50 ? 'bg-warning' : 'bg-success')}" style="width: ${f.properties.risk_score}%"></div>
                        </div>
                    </button>
                `
            })
            html += `</div></div>`
        }

        if (html === "") {
            html = `<div class="flex flex-col items-center justify-center p-8 text-center opacity-40">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 9.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="text-xs font-medium">${labels.no_data}</p>
            </div>`
        }

        this.listTarget.innerHTML = html
    }

    renderSidebarItem(f) {
        const props = f.properties
        const riskColorClass = props.risk_score >= 75 ? 'bg-error' : (props.risk_score >= 50 ? 'bg-warning' : 'bg-success')
        const riskTextColorClass = props.risk_score >= 75 ? 'text-error' : (props.risk_score >= 50 ? 'text-warning' : 'text-success')

        return `
            <button class="flex items-center gap-3 w-full text-left p-2 rounded-lg hover:bg-base-200 transition-colors group border border-transparent hover:border-base-300"
                    data-action="click->map#focusElement"
                    data-id="${props.id}">
                <div class="relative flex-none">
                    <div class="w-2 h-2 rounded-full ${riskColorClass} ring-4 ring-base-100 group-hover:ring-base-200 transition-all"></div>
                </div>
                <div class="flex-1 min-w-0">
                    <div class="text-xs font-bold truncate">${props.display_name}</div>
                    <div class="text-[10px] opacity-50 truncate flex items-center gap-2">
                        <span>${props.type === 'user' ? 'Site' : 'Vendor'}</span>
                        <span class="w-1 h-1 rounded-full bg-base-content/20"></span>
                        <span class="${riskTextColorClass} font-bold">${(props.risk_score != null) ? props.risk_score.toFixed(1) : 'N/A'} Risk</span>
                    </div>
                </div>
                <div class="flex-none opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                </div>
            </button>
        `
    }

    focusElement(event) {
        const id = event.currentTarget.dataset.id
        const layer = this.layers[id]
        if (!layer) return

        if (layer instanceof L.Marker) {
            if (this.markersCluster && this.markersCluster.hasLayer(layer)) {
                this.markersCluster.zoomToShowLayer(layer, () => {
                    layer.openTooltip()
                })
            } else {
                this.map.setView(layer.getLatLng(), 14, { animate: true })
                layer.openTooltip()
            }
        } else if (layer instanceof L.Polyline) {
            this.map.fitBounds(layer.getBounds(), { padding: [100, 100], animate: true })
            layer.openPopup()
        }
    }

    filterList(event) {
        const query = event.target.value
        this.populateSidebar(this.geoJsonData, query)
    }

    bindRoutePopup(feature, layer) {
        const props = feature.properties
        const content = `
            <div class="p-3 min-w-[320px]">
                <div class="text-[10px] uppercase opacity-50 font-bold mb-1">Logistics Route</div>
                <div class="font-bold border-b border-base-200 pb-1 mb-1 text-sm">
                    ${props.vendor_name} → ${props.plant_name}
                </div>
                <div class="flex justify-between text-xs">
                    <span>Quota:</span>
                    <span class="font-mono">${props.quota}%</span>
                </div>
                <div class="flex justify-between text-xs mt-0.5">
                    <span>Risk:</span>
                    <span class="font-bold ${props.risk_score >= 75 ? 'text-error' : (props.risk_score >= 50 ? 'text-warning' : 'text-success')}">
                        ${(props.risk_score != null) ? props.risk_score.toFixed(1) : 'N/A'}
                    </span>
                </div>
            </div>
        `
        layer.bindPopup(content, { className: 'premium-popup', closeButton: false, minWidth: 320 })

        // Hover effects for the line
        layer.on('mouseover', () => {
            layer.setStyle({ opacity: 1, weight: 4, dashArray: '' })
        })
        layer.on('mouseout', () => {
            layer.setStyle({ opacity: 0.6, weight: 2, dashArray: '5, 10' })
        })
    }

    createCustomIcon(feature) {
        const riskScore = feature.properties.risk_score || 0
        const type = feature.properties.type

        // Marker colors
        let color = "#3b82f6" // default blue
        let ping = false

        if (type === 'user') {
            color = "#4f46e5" // Indigo (Primary)
        } else {
            if (riskScore >= 75) {
                color = "#ef4444" // Red (Error)
                ping = true
            } else if (riskScore >= 50) {
                color = "#f59e0b" // Amber (Warning)
            } else {
                color = "#10b981" // Emerald (Success)
            }
        }

        const html = `
            <div style="display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; position: relative;">
                ${ping ? `<div style="position: absolute; width: 100%; height: 100%; border-radius: 50%; background-color: ${color}; opacity: 0.6; animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>` : ''}
                <div style="width: 14px; height: 14px; border-radius: 50%; background-color: ${color}; border: 3px solid white; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06); transition: transform 0.2s;"></div>
            </div>
            <style>
                @keyframes ping {
                    75%, 100% { transform: scale(2.5); opacity: 0; }
                }
            </style>
        `

        return L.divIcon({
            className: "custom-map-marker",
            html: html,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, -12]
        })
    }

    bindTooltipAndClick(feature, layer) {
        const props = feature.properties

        const typeLabel = props.type === 'user' ? 'Site' : 'Vendor'
        const riskClass = props.risk_score >= 75 ? 'text-error' : (props.risk_score >= 50 ? 'text-warning' : 'text-success')
        const riskVal = (props.risk_score != null) ? props.risk_score.toFixed(1) : 'N/A'
        const riskLabel = props.type === 'user' ? 'Agg. Risk' : 'Risk'

        const tooltipContent = `
            <div class="flex flex-col p-1 text-xs">
                <span class="font-bold mb-1">${props.display_name}</span>
                <div class="flex items-center gap-2 opacity-80 text-[10px]">
                    <span class="uppercase tracking-wider font-semibold">${typeLabel}</span>
                    <span class="w-1 h-1 rounded-full bg-base-content/40"></span>
                    <span class="${riskClass} font-bold">${riskVal} ${riskLabel}</span>
                </div>
            </div>
        `

        layer.bindTooltip(tooltipContent, {
            className: 'premium-tooltip shadow-xl border border-base-200 rounded-box bg-base-100/90 backdrop-blur-md',
            direction: 'top',
            offset: [0, -10],
            opacity: 1
        })

        layer.on('click', () => {
            if (props.details_url) {
                window.location.href = props.details_url
            }
        })
    }
}
